import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LoaderSymbol } from '../../components/loading';

/**
 * ReportProgressOverlay — Tier-1 deterministic progress bar.
 *
 * Drives a CSS animation from 0 → 85% over ~25 s, then holds.  When the
 * parent unmounts the component (preview finished), the overlay disappears
 * — in practice the user just sees the bar fly to the right and the
 * download trigger.
 *
 * User feedback 5/30/2026 #9: "Check whether we can add a dynamic progress
 * bar when previewing a large report."  Replaces the previous bottom-right
 * spinner toast with a full-screen overlay that's impossible to miss.
 */
// Remembers each report's last preview duration (ms) so the bar PACES to
// roughly that time next time — "speed based on the actual preview time" (§18.6).
const lastDurations = new Map<string, number>();

export function ReportProgressOverlay({ label, onStop }: {
  label: string;
  /** Real cancel handler (Reporting-Module-Prototype 2026-07-03 fix #3): when
   *  provided, a Stop button renders on the progress card and genuinely aborts
   *  the render (AbortController in the caller) — never just hides the bar. */
  onStop?: () => void;
}) {
  const [pct, setPct] = useState(0);
  // Elapsed seconds — user feedback 2026-05-31 wants the live clock visible
  // alongside the % so they know whether a "stuck at 85%" generation is
  // actually still working or hung.  Updated once per second via setInterval.
  const [elapsedSec, setElapsedSec] = useState(0);
  const startedAt = useRef<number>(performance.now());

  useEffect(() => {
    startedAt.current = performance.now();
    setPct(0);
    setElapsedSec(0);
    let raf = 0;
    // Pace to this report's LAST preview duration (clamped 4–60 s) so the bar's
    // speed matches reality; the first time uses a 12 s estimate.
    const targetMs = Math.max(4000, Math.min(60000, lastDurations.get(label) ?? 12000));
    const ceiling  = 85;        // hold at 85% until the request completes
    const tick = () => {
      const elapsed = performance.now() - startedAt.current;
      // Ease-out curve so progress feels fast at the start, slow at the end.
      const t = Math.min(1, elapsed / targetMs);
      const next = Math.min(ceiling, Math.round(ceiling * (1 - Math.pow(1 - t, 2))));
      setPct(next);
      if (next < ceiling) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    // Separate interval ticks the elapsed-seconds counter so it keeps
    // counting even AFTER the % bar hits its 85% ceiling.
    const secTimer = window.setInterval(() => {
      setElapsedSec(Math.floor((performance.now() - startedAt.current) / 1000));
    }, 1000);
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(secTimer);
      // Record this preview's real duration to pace the next one.
      lastDurations.set(label, performance.now() - startedAt.current);
    };
  }, [label]);

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15, 23, 42, 0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9500,
      }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10,
          boxShadow: '0 16px 48px rgba(0,0,0,.28)',
          padding: '22px 26px',
          minWidth: 380, maxWidth: 480,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LoaderSymbol size={22} />
          <strong style={{ fontSize: 14, color: 'var(--ai-ink)' }}>{label}</strong>
        </div>
        <div style={{
          height: 8, background: 'var(--ai-loader-track)', borderRadius: 4, overflow: 'hidden',
        }}>
          <div className="ld-pbar-fill" style={{
            width: pct + '%', height: '100%',
            transition: 'width .4s ease-out',
          }} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--ai-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ color: 'var(--ai-progress-color)', fontSize: 13 }}>{pct}%</strong>
            {/* token-exempt: semantic blue "elapsed" info pill (no matching --ai-* token). */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#eff6ff', border: '1px solid #bfdbfe',
              color: '#1d4ed8', padding: '2px 8px', borderRadius: 10,
              fontSize: 11, fontWeight: 600,
            }}>
              <i className="ti ti-clock" style={{ fontSize: 11 }} />
              {elapsedSec}s elapsed
            </span>
          </span>
          <span>Please wait — large reports can take up to a minute.</span>
        </div>
        {onStop && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="ca-btn" onClick={onStop}
                    title="Stop generating — cancels the render request.">
              <span className="tb-i" aria-hidden="true">⏹</span>&nbsp;Stop
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
